import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import {
  Commitment,
  Relationship,
} from "@/services/backend/relationships/types/relationship";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/shared/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { useRelationshipsRelationshipCreateAndAssignDocumentMutation } from "@/services/backend/relationships/api/service";
import { CommonSubmitButton } from "@/shared/components/button-common";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { VbsDocumentPage } from "@/routes/customers/[customerId]/relationships/[relationshipId]/commitments/[commitmentId]/_components/vbs-document-page";
import { RltDocumentPage } from "@/routes/customers/[customerId]/relationships/[relationshipId]/commitments/[commitmentId]/_components/rlt-document-page";

export function DocumentCard({
  relationship,
  commitment,
}: {
  relationship: Relationship;
  commitment: Commitment;
}) {
  return (
    <PersistenceContextProvider>
      <Card>
        <CardHeader>
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <CardTitle>{t("Arbeiten")}</CardTitle>
            {commitment.documentId && (
              <div className="flex h-4 items-center justify-end gap-4">
                <PersistenceStateUsingContext className="h-4" />
              </div>
            )}
          </div>
          <CardDescription>
            {t(
              "Diese Arbeiten müssen zur Erfüllung der Sollstellung durchgeführt werden.",
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {commitment.documentId === null ? (
            <NoDocumentPage
              relationship={relationship}
              commitment={commitment}
            />
          ) : (
            <DocumentPage relationship={relationship} commitment={commitment} />
          )}
        </CardContent>
      </Card>
    </PersistenceContextProvider>
  );
}

function NoDocumentPage({
  relationship,
  commitment,
}: {
  relationship: Relationship;
  commitment: Commitment;
}) {
  const [post, { isLoading, error, reset }] =
    useRelationshipsRelationshipCreateAndAssignDocumentMutation();

  const doCreate = () => {
    if (isLoading) {
      return;
    }

    post({
      id: relationship.id,
      commitmentId: commitment.id,
      serviceKind: commitment.serviceKind,
    });
  };

  return (
    <div className="grid gap-4">
      <Alert>
        <AlertTriangle />
        <AlertTitle>{t("Kein Arbeitsschein")}</AlertTitle>
        <AlertDescription>
          {t("Der Sollstellung wurde noch kein Arbeitsschein zugewiesen.")}
        </AlertDescription>
      </Alert>
      <RTKQueryErrorAlert
        error={error}
        onDismiss={() => reset()}
        ignoreValidationError={false}
      />
      <div className="grid gap-2">
        <CommonSubmitButton isLoading={isLoading} onClick={doCreate}>
          {t(`${commitment.serviceKind} Arbeitsschein erstellen`)}
        </CommonSubmitButton>
      </div>
    </div>
  );
}

function DocumentPage({
  relationship,
  commitment,
}: {
  relationship: Relationship;
  commitment: Commitment;
}) {
  if (commitment.documentId == null) {
    return null;
  }

  switch (commitment.serviceKind) {
    case GSMServiceKind.RLT:
      return (
        <RltDocumentPage relationship={relationship} commitment={commitment} />
      );
    case GSMServiceKind.VBS:
      return (
        <VbsDocumentPage relationship={relationship} commitment={commitment} />
      );
    default:
      return null;
  }
}
